import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheckCircle, faGift } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { flushSync } from 'react-dom';
import { matchPath, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { stripPrefix } from '@sorare/core/src/i18n/useTranslations';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import { FixtureOverview } from './FixtureOverview';
import { UpcomingFixtureOverview } from './UpcomingFixtureOverview';
import { FixtureTeamsOverview_so5Fixture } from './__generated__/index.graphql';

const DropdownContent = styled(Vertical)`
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  padding: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: min-content;
    border-radius: var(--double-unit);

    &.transparent {
      border: 1px solid var(--c-nd-150);
    }
  }

  &.useProOverview20 {
    @media ${laptopAndAbove} {
      padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit);
    }
  }
`;

const scrollToContender = (contenderSlug: string) => {
  const firstContenderNode = document.getElementById(contenderSlug);
  if (firstContenderNode) {
    flushSync(() => {
      firstContenderNode.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }
};

type Props = {
  so5Fixture: FixtureTeamsOverview_so5Fixture;
  trapClickOutside?: boolean;
  transparent?: boolean;
};

export const FixtureTeamsOverview = ({
  so5Fixture,
  trapClickOutside,
  transparent = true,
}: Props) => {
  const {
    flags: { useProOverview20 = false },
  } = useFeatureFlags();
  const location = useLocation();
  const navigate = useNavigate();
  const { sport } = useSportContext();
  const track = useEvents();
  const isUpcoming = isFixtureOpened(so5Fixture);

  const so5LeaderboardGroupInterfacesWithContendersNotTraining =
    so5Fixture.so5LeaderboardGroups.filter(
      so5LeaderboardGroup =>
        so5LeaderboardGroup.mySo5LeaderboardContenders.filter(
          contender =>
            isUpcoming ||
            contender.so5Leaderboard.so5TournamentType.so5LeaderboardType !==
              So5LeaderboardType.SPECIAL_TRAINING_CENTER
        ).length
    );

  const { total, composed, rewarded } =
    so5LeaderboardGroupInterfacesWithContendersNotTraining
      .flatMap(league => league.mySo5LeaderboardContenders)
      .reduce(
        (acc, contender) => {
          return {
            composed:
              acc.composed + (contender?.so5Lineup?.confirmable ? 1 : 0),
            rewarded:
              acc.rewarded +
              (contender?.so5Lineup?.so5Rankings.at(0)?.eligibleOrSo5Rewards
                .length && hasGamesStarted(contender.so5Lineup)
                ? 1
                : 0),
            total: acc.total + 1,
          };
        },
        { composed: 0, rewarded: 0, total: 0 }
      );

  const icon = isUpcoming ? (
    <FontAwesomeIcon
      icon={faCheckCircle}
      color={composed === total ? 'var(--c-rivals-win)' : 'var(--c-nd-600)'}
    />
  ) : (
    <FontAwesomeIcon
      icon={faGift}
      color={rewarded ? 'var(--c-rivals-win)' : 'var(--c-nd-600)'}
    />
  );

  if (!total) {
    return null;
  }

  const goToContender = (value: string) => {
    const path = `${generateSportPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
      params: {
        fixture: so5Fixture.slug || '',
        eventType: so5Fixture.type.toLowerCase(),
      },
      sport,
    })}`;

    if (matchPath(path, stripPrefix(location.pathname || ''))) {
      scrollToContender(value);
    } else {
      navigate(`${path}#${value}`);
    }
  };

  return (
    <Dropdown
      transparent={transparent}
      gap={8}
      trapClickOutside={trapClickOutside}
      onOpen={() => {
        track('Open Fixture Overview', {
          so5FixtureSlug: so5Fixture.slug,
          so5FixtureState: so5Fixture.aasmState,
        });
      }}
      label={({ open, ...props }) => (
        <Button
          color="tertiary"
          size="medium"
          className={classNames({ active: open })}
          {...props}
        >
          <Horizontal as="span">
            {icon}
            {isUpcoming ? composed : rewarded}
            {' / '}
            {total}
          </Horizontal>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent
          className={classNames({ useProOverview20, transparent })}
        >
          {isUpcoming ? (
            <UpcomingFixtureOverview
              so5Fixture={so5Fixture}
              onClose={closeDropdown}
              scrollToContender={goToContender}
            />
          ) : (
            <FixtureOverview
              so5Fixture={so5Fixture}
              onClose={closeDropdown}
              scrollToContender={goToContender}
            />
          )}
        </DropdownContent>
      )}
    </Dropdown>
  );
};

FixtureTeamsOverview.fragments = {
  so5Fixture: gql`
    fragment FixtureTeamsOverview_so5Fixture on So5Fixture {
      slug
      aasmState
      type
      so5LeaderboardGroups {
        slug
        mySo5LeaderboardContenders {
          slug
          so5Leaderboard {
            slug
            so5TournamentType {
              id
              so5LeaderboardType
            }
          }
          so5Lineup {
            id
            confirmable
            so5Rankings {
              id
              eligibleOrSo5Rewards {
                ... on So5Reward {
                  id
                  slug
                }
              }
            }
            ...hasGamesStarted_so5Lineup
          }
        }
      }
      ...UpcomingFixtureOverview_so5Fixture
      ...FixtureOverview_so5Fixture
    }
    ${UpcomingFixtureOverview.fragments.so5Fixture}
    ${FixtureOverview.fragments.so5Fixture}
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<FixtureTeamsOverview_so5Fixture>,
};
